import { GatsbyImage } from "gatsby-plugin-image";
import startCase from "lodash-es/startCase";
import React, { useState } from "react";
import Lightbox, { ImagesListType } from "react-spring-lightbox";
import styled, { css } from "styled-components";
import { useToggle } from "../../shared/use-toggle";
import { ProductCategoryTitle } from "./ProductCategoryTitle";
import { ProductCategoryImage } from "./types";
import {
  faChevronRight,
  faChevronLeft,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  grid-auto-rows: 280px;
  grid-gap: 20px;
`;

function getImageCaption(image: ProductCategoryImage): string {
  const { title, filename } = image!;

  if (title) {
    return title;
  }

  const splitted = filename?.split(".");

  if (Array.isArray(splitted) && splitted.length > 1) {
    return startCase(splitted[0]);
  }

  return "";
}

interface ProductCategoryGalleryProps {
  images: ProductCategoryImage[];
}

export const ProductCategoryGallery: React.FC<ProductCategoryGalleryProps> = (
  props
) => {
  const [modalOpened, toggleModal] = useToggle();
  const [currentImageIndex, setCurrentIndex] = useState(0);

  const parsedImages = parseImages(props.images);

  function gotoPrevious(): void {
    currentImageIndex > 0 && setCurrentIndex(currentImageIndex - 1);
  }

  function gotoNext(): void {
    currentImageIndex + 1 < parsedImages.length &&
      setCurrentIndex(currentImageIndex + 1);
  }

  function openLigthbox(index: number): void {
    toggleModal();
    setCurrentIndex(index);
  }

  return (
    <div>
      <ProductCategoryTitle>Immagini</ProductCategoryTitle>
      <Grid>
        {props.images.map((image, i) => (
          <div
            key={i}
            onClick={() => {
              openLigthbox(i);
            }}
          >
            <GatsbyImage
              image={image!.thumb!}
              style={{ width: "100%", height: "100%", cursor: "pointer" }}
              alt={getImageCaption(image)}
            />
          </div>
        ))}
      </Grid>
      <StyledLightbox
        isOpen={modalOpened}
        onPrev={gotoPrevious}
        onNext={gotoNext}
        onClose={toggleModal}
        images={parsedImages}
        currentIndex={currentImageIndex}
        renderNextButton={() =>
          currentImageIndex + 1 < parsedImages.length && (
            <RightButton onClick={gotoNext}>
              <FontAwesomeIcon icon={faChevronRight} fixedWidth />
            </RightButton>
          )
        }
        renderPrevButton={() =>
          currentImageIndex > 0 && (
            <LeftButton onClick={gotoPrevious}>
              <FontAwesomeIcon icon={faChevronLeft} fixedWidth />
            </LeftButton>
          )
        }
        renderHeader={() => (
          <LightboxHeader
            images={parsedImages}
            currentIndex={currentImageIndex}
            onClose={toggleModal}
          />
        )}
      />
    </div>
  );
};

function parseImages(images: ProductCategoryImage[]): ImagesListType {
  return images!.map((image) => {
    return {
      src: image!.full!.images.fallback.src,
      srcSet: image!.full!.images.fallback.srcSet,
      loading: "lazy",
      alt: getImageCaption(image),
    };
  });
}

interface LightboxHeaderProps {
  images: ImagesListType;
  currentIndex: number;
  onClose: () => void;
}

function LightboxHeader({
  images,
  currentIndex,
  onClose,
}: LightboxHeaderProps): JSX.Element {
  return (
    <HeaderContainer>
      <HeaderTitle>{images[currentIndex].alt}</HeaderTitle>
      <HeaderCloseButton onClick={onClose}>
        <FontAwesomeIcon icon={faTimes} fixedWidth />
      </HeaderCloseButton>
    </HeaderContainer>
  );
}

const StyledLightbox = styled(Lightbox)`
  background-color: rgba(0, 0, 0, 0.85);
  z-index: 20000;
`;

const commonButtonProps = css`
  position: absolute;
  z-index: 1;
  background: rgba(0, 0, 0, 0.2);
  border: 0;
  border-radius: 50%;
  color: white;
  width: 50px;
  height: 50px;
`;

const RightButton = styled.button`
  ${commonButtonProps}
  right: 10px;
`;

const LeftButton = styled.button`
  ${commonButtonProps}
  left: 10px;
`;

const HeaderContainer = styled.div`
  display: flex;
  z-index: 1;
`;

const HeaderTitle = styled.div`
  flex: 1;
  line-height: 50px;
  color: white;
  padding: 0 20px;
`;

const HeaderCloseButton = styled.button`
  border: 0;
  background: transparent;
  color: white;
  width: 50px;
  height: 50px;
`;
